import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : `/${path}`) : path);

const HL_WS_URL = 'wss://api.hyperliquid.xyz/ws';
const DEFAULT_COIN = 'CASHCAT';
const TRADE_URL = 'https://app.hyperliquid.xyz/trade/CASHCAT';
const INTERVAL = '1m';
const DEFAULT_MULTIPLIER = 10;
const DEFAULT_MIN_PREV_VOLUME = 1;
const DEFAULT_ABS_VOLUME = 2_000_000;
/** 规则A额外要求：当前分钟成交量至少达到该值（与倍数条件同时满足） */
const DEFAULT_MULTIPLE_MIN_VOLUME = 1_000_000;
const DEFAULT_COOLDOWN_SEC = 60;
const MAX_CANDLES = 60;
const MAX_ALERTS = 40;
const PING_MS = 20000;

function minuteOpenMs(ts = Date.now()) {
  return Math.floor(ts / 60000) * 60000;
}

function formatTime(ms) {
  if (!Number.isFinite(ms)) return '-';
  return new Date(ms).toLocaleTimeString('zh-CN', { hour12: false });
}

function formatNum(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('zh-CN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatPrice(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n >= 1) return n.toFixed(4);
  if (n >= 0.01) return n.toFixed(6);
  return n.toFixed(8);
}

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

function normalizeCandle(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const t = Number(raw.t);
  const v = Number(raw.v);
  const c = Number(raw.c);
  const o = Number(raw.o);
  if (!Number.isFinite(t) || !Number.isFinite(v)) return null;
  return {
    t,
    T: Number(raw.T) || t + 60000,
    s: String(raw.s || ''),
    i: String(raw.i || INTERVAL),
    o: Number.isFinite(o) ? o : null,
    c: Number.isFinite(c) ? c : null,
    h: Number(raw.h),
    l: Number(raw.l),
    v,
    n: Number(raw.n) || 0,
  };
}

function extractCandles(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload.map(normalizeCandle).filter(Boolean);
  if (payload.data != null) return extractCandles(payload.data);
  const one = normalizeCandle(payload);
  return one ? [one] : [];
}

function statusTone(status) {
  if (status === 'live') return 'ok';
  if (status === 'arming' || status === 'connecting') return 'warm';
  if (status === 'error' || status === 'closed') return 'bad';
  return 'muted';
}

function statusLabel(status) {
  if (status === 'connecting') return '连接中';
  if (status === 'arming') return '冷启动中';
  if (status === 'live') return '实时监控中';
  if (status === 'closed') return '已断开';
  if (status === 'error') return '连接异常';
  return status || '-';
}

function formatCompactVol(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return formatNum(n, 2);
}

function alertTypeLabel(type) {
  if (type === 'absolute') return '单分钟绝对量';
  return '相对前分钟倍数';
}

export default function VolumeSpikePage() {
  const [coin] = useState(DEFAULT_COIN);
  const [multiplier, setMultiplier] = useState(DEFAULT_MULTIPLIER);
  const [minPrevVolume, setMinPrevVolume] = useState(DEFAULT_MIN_PREV_VOLUME);
  const [absVolume, setAbsVolume] = useState(DEFAULT_ABS_VOLUME);
  const [multipleMinVolume, setMultipleMinVolume] = useState(DEFAULT_MULTIPLE_MIN_VOLUME);
  const [cooldownSec, setCooldownSec] = useState(DEFAULT_COOLDOWN_SEC);
  const [status, setStatus] = useState('connecting');
  const [lastError, setLastError] = useState('');
  const [candles, setCandles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [lastEventAt, setLastEventAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [cloudStatus, setCloudStatus] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudMessage, setCloudMessage] = useState('');
  const [cloudBusy, setCloudBusy] = useState('');
  const [settingsReady, setSettingsReady] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const candlesMapRef = useRef(new Map());
  const seededRef = useRef(false);
  const armedRef = useRef(false);
  const connectAtRef = useRef(0);
  const lastMultipleAlertKeyRef = useRef('');
  const lastMultipleAlertAtRef = useRef(0);
  const lastAbsAlertKeyRef = useRef('');
  const lastAbsAlertAtRef = useRef(0);
  const settingsSaveTimerRef = useRef(null);
  const settingsHydratedRef = useRef(false);
  const applyingRemoteRef = useRef(false);
  const settingsRef = useRef({ multiplier, minPrevVolume, absVolume, multipleMinVolume, cooldownSec });

  useEffect(() => {
    settingsRef.current = { multiplier, minPrevVolume, absVolume, multipleMinVolume, cooldownSec };
  }, [multiplier, minPrevVolume, absVolume, multipleMinVolume, cooldownSec]);

  const applySettingsFromCloud = useCallback((data) => {
    if (!data || typeof data !== 'object') return;
    applyingRemoteRef.current = true;
    if (data.multiplier != null && Number.isFinite(Number(data.multiplier))) {
      setMultiplier(Math.max(1, Number(data.multiplier)));
    }
    if (data.minPrevVolume != null && Number.isFinite(Number(data.minPrevVolume))) {
      setMinPrevVolume(Math.max(0, Number(data.minPrevVolume)));
    }
    if (data.absVolume != null && Number.isFinite(Number(data.absVolume))) {
      setAbsVolume(Math.max(0, Number(data.absVolume)));
    }
    if (data.multipleMinVolume != null && Number.isFinite(Number(data.multipleMinVolume))) {
      setMultipleMinVolume(Math.max(0, Number(data.multipleMinVolume)));
    }
    if (data.cooldownSec != null && Number.isFinite(Number(data.cooldownSec))) {
      setCooldownSec(Math.max(5, Number(data.cooldownSec)));
    }
    window.setTimeout(() => {
      applyingRemoteRef.current = false;
    }, 0);
  }, []);

  const saveSettingsToCloud = useCallback(async (partial = {}, { quiet = false } = {}) => {
    const next = {
      coin: DEFAULT_COIN,
      multiplier: partial.multiplier ?? settingsRef.current.multiplier,
      absVolume: partial.absVolume ?? settingsRef.current.absVolume,
      minPrevVolume: partial.minPrevVolume ?? settingsRef.current.minPrevVolume,
      multipleMinVolume: partial.multipleMinVolume ?? settingsRef.current.multipleMinVolume,
      cooldownSec: partial.cooldownSec ?? settingsRef.current.cooldownSec,
      ...partial,
    };
    if (!quiet) setCloudBusy('settings');
    try {
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ volumeSpike: next }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `保存失败 ${resp.status}`);
      setSettingsDirty(false);
      if (!quiet) setCloudMessage('阈值已保存，云端 Cron / 企微通知将按新参数执行');
      await loadCloudStatusRef.current?.({ skipApplySettings: true });
      return true;
    } catch (err) {
      if (!quiet) setCloudMessage(err?.message || '阈值保存失败');
      return false;
    } finally {
      if (!quiet) setCloudBusy('');
    }
  }, []);

  const loadCloudStatusRef = useRef(null);

  const loadCloudStatus = useCallback(async (opts = {}) => {
    setCloudLoading(true);
    try {
      const resp = await fetch(api('/api/volume-spike/status'), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `状态读取失败 ${resp.status}`);
      const data = body.data || null;
      setCloudStatus(data);
      if (data && !opts.skipApplySettings) {
        applySettingsFromCloud(data);
        settingsHydratedRef.current = true;
        setSettingsReady(true);
      }
      if (!opts.keepMessage) setCloudMessage('');
    } catch (err) {
      setCloudMessage(err?.message || '云端状态读取失败');
      if (!settingsHydratedRef.current) {
        settingsHydratedRef.current = true;
        setSettingsReady(true);
      }
    } finally {
      setCloudLoading(false);
    }
  }, [applySettingsFromCloud]);

  loadCloudStatusRef.current = loadCloudStatus;

  useEffect(() => {
    loadCloudStatus();
    const timer = window.setInterval(() => loadCloudStatus({ skipApplySettings: true, keepMessage: true }), 30000);
    return () => window.clearInterval(timer);
  }, [loadCloudStatus]);

  // 阈值变更后自动防抖保存到云端（供 Cron 同步）
  useEffect(() => {
    if (!settingsHydratedRef.current || !settingsReady || applyingRemoteRef.current) return undefined;
    setSettingsDirty(true);
    if (settingsSaveTimerRef.current) window.clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = window.setTimeout(() => {
      if (applyingRemoteRef.current) return;
      saveSettingsToCloud({}, { quiet: false });
    }, 800);
    return () => {
      if (settingsSaveTimerRef.current) window.clearTimeout(settingsSaveTimerRef.current);
    };
  }, [multiplier, minPrevVolume, absVolume, multipleMinVolume, cooldownSec, settingsReady, saveSettingsToCloud]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const publishCandles = useCallback(() => {
    const list = Array.from(candlesMapRef.current.values()).sort((a, b) => a.t - b.t);
    setCandles(list.slice(-MAX_CANDLES));
  }, []);

  const pushAlert = useCallback((item) => {
    setAlerts((prev) => [item, ...prev].slice(0, MAX_ALERTS));
  }, []);

  const maybeMultipleAlert = useCallback((current, previous) => {
    if (!armedRef.current || !current || !previous) return;
    const { multiplier: mult, minPrevVolume: floor, multipleMinVolume: currFloor, cooldownSec: cool } = settingsRef.current;
    const prevVol = Number(previous.v) || 0;
    const currVol = Number(current.v) || 0;
    // 规则A：倍数达标 + 当前分钟成交量 ≥ 1M（可配置）同时满足
    if (prevVol < floor || currVol < prevVol * mult || currVol < currFloor) return;

    const alertKey = `multiple:${current.t}:${previous.t}`;
    const now = Date.now();
    if (alertKey === lastMultipleAlertKeyRef.current) return;
    if (now - lastMultipleAlertAtRef.current < cool * 1000) return;

    const prevClose = Number(previous.c);
    const currClose = Number(current.c);
    const priceChangePct = Number.isFinite(prevClose) && prevClose !== 0 && Number.isFinite(currClose)
      ? ((currClose - prevClose) / prevClose) * 100
      : null;

    lastMultipleAlertKeyRef.current = alertKey;
    lastMultipleAlertAtRef.current = now;
    pushAlert({
      id: `multiple-${current.t}-${now}`,
      type: 'multiple',
      at: now,
      coin,
      currentT: current.t,
      previousT: previous.t,
      currentVolume: currVol,
      previousVolume: prevVol,
      multiple: prevVol > 0 ? currVol / prevVol : null,
      currentPrice: currClose,
      previousPrice: prevClose,
      priceChangePct,
    });
  }, [coin, pushAlert]);

  const maybeAbsoluteAlert = useCallback((current, previous) => {
    if (!armedRef.current || !current) return;
    const { absVolume: threshold, cooldownSec: cool } = settingsRef.current;
    const currVol = Number(current.v) || 0;
    if (!(currVol >= threshold)) return;

    // 同一根分钟只报一次，与倍数警报互不影响
    const alertKey = `absolute:${current.t}`;
    const now = Date.now();
    if (alertKey === lastAbsAlertKeyRef.current) return;
    if (now - lastAbsAlertAtRef.current < cool * 1000) return;

    const prevClose = Number(previous?.c);
    const currClose = Number(current.c);
    const priceChangePct = Number.isFinite(prevClose) && prevClose !== 0 && Number.isFinite(currClose)
      ? ((currClose - prevClose) / prevClose) * 100
      : null;

    lastAbsAlertKeyRef.current = alertKey;
    lastAbsAlertAtRef.current = now;
    pushAlert({
      id: `absolute-${current.t}-${now}`,
      type: 'absolute',
      at: now,
      coin,
      currentT: current.t,
      previousT: previous?.t ?? null,
      currentVolume: currVol,
      previousVolume: Number(previous?.v) || null,
      multiple: null,
      threshold,
      currentPrice: currClose,
      previousPrice: Number.isFinite(prevClose) ? prevClose : null,
      priceChangePct,
    });
  }, [coin, pushAlert]);

  const ingestCandles = useCallback((incoming, { fromSnapshot = false } = {}) => {
    if (!incoming.length) return;
    const map = candlesMapRef.current;
    let changed = false;
    for (const candle of incoming) {
      const prev = map.get(candle.t);
      if (!prev || prev.v !== candle.v || prev.c !== candle.c || prev.n !== candle.n) {
        map.set(candle.t, candle);
        changed = true;
      }
    }
    while (map.size > MAX_CANDLES + 10) {
      const oldest = Array.from(map.keys()).sort((a, b) => a - b)[0];
      map.delete(oldest);
    }
    if (!changed) return;

    setLastEventAt(Date.now());
    publishCandles();

    // 冷启动：订阅后首批/快照只建基线，不播报历史异动
    if (!seededRef.current) {
      seededRef.current = true;
      setStatus('arming');
      return;
    }

    const nowOpen = minuteOpenMs();
    const current = map.get(nowOpen) || null;
    const previous = map.get(nowOpen - 60000) || null;

    // 必须等到连接后出现「当前分钟」的实时更新，才解除冷启动
    if (!armedRef.current) {
      const liveTouch = incoming.some((c) => c.t === nowOpen && c.t >= minuteOpenMs(connectAtRef.current));
      if (liveTouch || (!fromSnapshot && current)) {
        armedRef.current = true;
        setStatus('live');
      } else {
        setStatus('arming');
        return;
      }
    } else {
      setStatus('live');
    }

    if (!fromSnapshot) {
      maybeMultipleAlert(current, previous);
      maybeAbsoluteAlert(current, previous);
    }
  }, [maybeAbsoluteAlert, maybeMultipleAlert, publishCandles]);

  const cleanupSocket = useCallback(() => {
    if (pingRef.current) {
      window.clearInterval(pingRef.current);
      pingRef.current = null;
    }
    if (reconnectRef.current) {
      window.clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }
    const ws = wsRef.current;
    wsRef.current = null;
    if (ws) {
      try {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      } catch (_) {}
    }
  }, []);

  const connect = useCallback(() => {
    cleanupSocket();
    seededRef.current = false;
    armedRef.current = false;
    connectAtRef.current = Date.now();
    setStatus('connecting');
    setLastError('');

    const ws = new WebSocket(HL_WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('arming');
      ws.send(JSON.stringify({
        method: 'subscribe',
        subscription: { type: 'candle', coin, interval: INTERVAL },
      }));
      pingRef.current = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ method: 'ping' }));
        }
      }, PING_MS);
    };

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!msg || typeof msg !== 'object') return;
      if (msg.channel === 'pong' || msg.channel === 'subscriptionResponse') return;
      if (msg.channel !== 'candle' && msg.channel !== 'candles') return;

      const list = extractCandles(msg);
      if (!list.length) return;
      // 首批往往是快照/多根历史；之后多为单根实时更新
      const fromSnapshot = !seededRef.current || list.length > 1;
      ingestCandles(list, { fromSnapshot });
    };

    ws.onerror = () => {
      setLastError('WebSocket 连接出错');
      setStatus('error');
    };

    ws.onclose = () => {
      setStatus((prev) => (prev === 'error' ? prev : 'closed'));
      if (pingRef.current) {
        window.clearInterval(pingRef.current);
        pingRef.current = null;
      }
      reconnectRef.current = window.setTimeout(() => {
        connect();
      }, 2500);
    };
  }, [cleanupSocket, coin, ingestCandles]);

  useEffect(() => {
    connect();
    return () => cleanupSocket();
  }, [connect, cleanupSocket]);

  async function saveCloudEnabled(enabled) {
    setCloudBusy('save');
    try {
      const resp = await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          volumeSpike: {
            enabled: !!enabled,
            coin: DEFAULT_COIN,
            multiplier,
            absVolume,
            minPrevVolume,
            multipleMinVolume,
            cooldownSec,
          },
        }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || `保存失败 ${resp.status}`);
      setCloudMessage(enabled ? '已开启云端 24h 监控（每分钟检查并推企业微信）' : '已关闭云端 24h 监控');
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '保存失败');
    } finally {
      setCloudBusy('');
    }
  }

  async function runCloudAction(kind) {
    setCloudBusy(kind);
    try {
      // 先把当前阈值落到云端，再跑 Cron，避免通知仍用旧参数
      await saveSettingsToCloud({}, { quiet: true });
      const qs = kind === 'test' ? '?test=1' : kind === 'dry' ? '?dry=1' : '';
      const resp = await fetch(api(`/api/cron/volume-spike${qs}`), { cache: 'no-store' });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || body.success === false) throw new Error(body?.message || body?.data?.reason || `执行失败 ${resp.status}`);
      const data = body.data || {};
      setCloudMessage(data.reason || '已执行');
      await loadCloudStatus({ skipApplySettings: true, keepMessage: true });
    } catch (err) {
      setCloudMessage(err?.message || '执行失败');
    } finally {
      setCloudBusy('');
    }
  }

  const currentOpen = minuteOpenMs(nowTick);
  const currentCandle = candles.find((c) => c.t === currentOpen) || null;
  const previousCandle = candles.find((c) => c.t === currentOpen - 60000) || null;

  const currentVol = Number(currentCandle?.v);
  const previousVol = Number(previousCandle?.v);
  const volumeMultiple = Number.isFinite(currentVol) && Number.isFinite(previousVol) && previousVol > 0
    ? currentVol / previousVol
    : null;
  const priceChangePct = Number.isFinite(Number(currentCandle?.c))
    && Number.isFinite(Number(previousCandle?.c))
    && Number(previousCandle.c) !== 0
    ? ((Number(currentCandle.c) - Number(previousCandle.c)) / Number(previousCandle.c)) * 100
    : null;
  const triggered = Number.isFinite(volumeMultiple) && volumeMultiple >= multiplier
    && Number.isFinite(previousVol) && previousVol >= minPrevVolume
    && Number.isFinite(currentVol) && currentVol >= multipleMinVolume;
  const absTriggered = Number.isFinite(currentVol) && currentVol >= absVolume;

  const maxBarVol = useMemo(() => {
    const recent = candles.slice(-20);
    return Math.max(1, ...recent.map((c) => Number(c.v) || 0), Number(currentVol) || 0, absVolume);
  }, [candles, currentVol, absVolume]);

  const recentBars = candles.slice(-20);

  return (
    <div className="volume-spike-page primary-arb-page">
      <section className="cxmt-hero">
        <div>
          <span className="cxmt-eyebrow">Secondary Volume Spike</span>
          <h1>二级量价异动</h1>
          <p>
            通过 Hyperliquid WebSocket 订阅 {coin} 的 1 分钟 K 线。冷启动只建立基线，不播报历史 K 线。
            两套独立警报：① 当前分钟量 ≥ 前 1 分钟 × {multiplier}，且当前分钟量 ≥ {formatCompactVol(multipleMinVolume)}；② 单根分钟量 ≥ {formatCompactVol(absVolume)}。
          </p>
        </div>
        <div className="cxmt-hero-actions">
          <a href={TRADE_URL} target="_blank" rel="noreferrer">打开 HYPE {coin}</a>
          <button type="button" onClick={connect}>重新连接</button>
        </div>
      </section>

      <section className="volume-spike-panel volume-spike-cloud">
        <div className="volume-spike-panel-head">
          <div>
            <h2>云端 24h 监控（企业微信）</h2>
            <p>
              关掉浏览器也会跑：挂在现有每分钟 Cron 上轮询 K 线，命中规则后推企业微信。
              Webhook 复用「持仓查询 → 机器人配置」里已同步的地址。
            </p>
          </div>
          <span className={`volume-spike-badge tone-${cloudStatus?.enabled && cloudStatus?.hasWeCom ? 'ok' : 'warm'}`}>
            {cloudLoading ? '读取中' : (cloudStatus?.enabled ? '云端已开启' : '云端未开启')}
          </span>
        </div>

        <div className="volume-spike-cloud-grid">
          <div>
            <span>企业微信</span>
            <strong>{cloudStatus?.hasWeCom ? '已配置' : '未配置'}</strong>
            <small>{cloudStatus?.hasWeCom ? '将复用站点机器人 Webhook' : '请先到持仓查询页同步 Webhook'}</small>
          </div>
          <div>
            <span>冷启动</span>
            <strong>{cloudStatus?.armed ? '已完成' : '待首次建基线'}</strong>
            <small>首次 Cron 只记基线，不推历史</small>
          </div>
          <div>
            <span>最近云端检查</span>
            <strong>{cloudStatus?.lastCheckAt ? formatTime(cloudStatus.lastCheckAt) : '-'}</strong>
            <small>最近报警 {cloudStatus?.lastAlertAt ? formatTime(cloudStatus.lastAlertAt) : '暂无'}</small>
          </div>
          <div>
            <span>云端最近量</span>
            <strong>{formatCompactVol(cloudStatus?.lastCurrentVol)}</strong>
            <small>前分钟 {formatCompactVol(cloudStatus?.lastPrevVol)}</small>
          </div>
        </div>

        <div className="volume-spike-cloud-actions">
          <button
            type="button"
            disabled={!!cloudBusy}
            onClick={() => saveCloudEnabled(!(cloudStatus?.enabled))}
          >
            {cloudBusy === 'save' ? '保存中…' : (cloudStatus?.enabled ? '关闭云端监控' : '开启云端监控')}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('run')}>
            {cloudBusy === 'run' ? '检查中…' : '立即检查一次'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('dry')}>
            {cloudBusy === 'dry' ? '干跑中…' : '干跑（不推送）'}
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => runCloudAction('test')}>
            {cloudBusy === 'test' ? '发送中…' : '发送企微测试文案'}
          </button>
          <button type="button" disabled={!!cloudBusy || cloudLoading} onClick={() => loadCloudStatus({ skipApplySettings: false })}>
            刷新状态
          </button>
          <button type="button" disabled={!!cloudBusy} onClick={() => saveSettingsToCloud()}>
            {cloudBusy === 'settings' ? '保存中…' : '立即保存阈值'}
          </button>
        </div>

        {cloudMessage && <div className="primary-arb-message">{cloudMessage}</div>}
        {cloudStatus?.lastError && (
          <div className="primary-arb-message">云端最近错误：{cloudStatus.lastError}</div>
        )}
      </section>

      <section className="cxmt-formula-card">
        <div>
          <b>规则 A · 相对倍数</b>
          <p>
            当前分钟 `v` ≥ 前一分钟 `v` × {multiplier}，且当前分钟成交量 ≥ {formatCompactVol(multipleMinVolume)}，
            同时前一分钟量 ≥ 地板值。附带相对前分钟收盘涨跌幅。
          </p>
        </div>
        <div>
          <b>规则 B · 绝对量（独立）</b>
          <p>
            单根 1 分钟 K 线成交量 ≥ {formatCompactVol(absVolume)} 即提醒，不依赖前一分钟倍数；同一分钟只报一次。
          </p>
        </div>
        <div>
          <b>状态</b>
          <p>
            <em className={`volume-spike-badge tone-${statusTone(status)}`}>{statusLabel(status)}</em>
            {lastError ? ` · ${lastError}` : ''}
            {lastEventAt ? ` · 最近事件 ${formatTime(lastEventAt)}` : ''}
          </p>
        </div>
      </section>

      <section className="cxmt-config volume-spike-config volume-spike-config-6">
        <label>
          量能倍数阈值
          <input
            type="number"
            min="1"
            step="1"
            value={multiplier}
            onChange={(e) => setMultiplier(Math.max(1, Number(e.target.value) || DEFAULT_MULTIPLIER))}
          />
        </label>
        <label>
          规则A当前量门槛
          <input
            type="number"
            min="0"
            step="100000"
            value={multipleMinVolume}
            onChange={(e) => setMultipleMinVolume(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label>
          前分钟量地板
          <input
            type="number"
            min="0"
            step="1"
            value={minPrevVolume}
            onChange={(e) => setMinPrevVolume(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label>
          单分钟绝对量阈值
          <input
            type="number"
            min="0"
            step="100000"
            value={absVolume}
            onChange={(e) => setAbsVolume(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <label>
          提醒冷却（秒）
          <input
            type="number"
            min="5"
            step="5"
            value={cooldownSec}
            onChange={(e) => setCooldownSec(Math.max(5, Number(e.target.value) || DEFAULT_COOLDOWN_SEC))}
          />
        </label>
        <div>
          <span>同步方式</span>
          <strong>WS 推送</strong>
          <small>
            {settingsDirty || cloudBusy === 'settings'
              ? (cloudBusy === 'settings' ? '正在同步到云端 Cron…' : '已修改，即将同步…')
              : '阈值已同步云端 Cron'}
          </small>
        </div>
      </section>

      <section className={`cxmt-metrics volume-spike-metrics ${triggered || absTriggered ? 'is-hot' : ''}`}>
        <div>
          <span>当前分钟成交量</span>
          <strong className={absTriggered ? 'hot-text' : ''}>{formatCompactVol(currentVol)}</strong>
          <small>{formatTime(currentOpen)} · 形成中</small>
        </div>
        <div>
          <span>前 1 分钟成交量</span>
          <strong>{formatCompactVol(previousVol)}</strong>
          <small>{formatTime(currentOpen - 60000)} · 已收盘</small>
        </div>
        <div>
          <span>量能倍数</span>
          <strong className={triggered ? 'hot-text' : ''}>
            {Number.isFinite(volumeMultiple) ? `${volumeMultiple.toFixed(2)}×` : '-'}
          </strong>
          <small>规则 A · ≥ {multiplier}× 且 ≥ {formatCompactVol(multipleMinVolume)}</small>
        </div>
        <div>
          <span>绝对量警报</span>
          <strong className={absTriggered ? 'hot-text' : ''}>
            {absTriggered ? '已触发' : '未触发'}
          </strong>
          <small>规则 B · ≥ {formatCompactVol(absVolume)}</small>
        </div>
      </section>

      <section className="cxmt-metrics volume-spike-metrics">
        <div>
          <span>相对前分钟涨跌</span>
          <strong className={Number(priceChangePct) > 0 ? 'up-text' : Number(priceChangePct) < 0 ? 'down-text' : ''}>
            {formatPct(priceChangePct)}
          </strong>
          <small>
            {formatPrice(previousCandle?.c)} → {formatPrice(currentCandle?.c)}
          </small>
        </div>
        <div>
          <span>当前精确成交量</span>
          <strong>{formatNum(currentVol, 2)}</strong>
          <small>原始数值</small>
        </div>
        <div>
          <span>规则 A 状态</span>
          <strong className={triggered ? 'hot-text' : ''}>{triggered ? '倍数异动中' : '观察中'}</strong>
          <small>与规则 B 互不影响</small>
        </div>
        <div>
          <span>规则 B 进度</span>
          <strong>
            {Number.isFinite(currentVol) && absVolume > 0
              ? `${Math.min(100, (currentVol / absVolume) * 100).toFixed(1)}%`
              : '-'}
          </strong>
          <small>相对 {formatCompactVol(absVolume)}</small>
        </div>
      </section>

      <section className="volume-spike-panel">
        <div className="volume-spike-panel-head">
          <div>
            <h2>1 分钟量能窗</h2>
            <p>仅展示本地缓存的最近分钟；高度按相对最大成交量缩放。红柱=当前，灰柱=前1分。</p>
          </div>
          <span className={`volume-spike-badge tone-${triggered || absTriggered ? 'bad' : statusTone(status)}`}>
            {absTriggered ? '绝对量触发中' : triggered ? '倍数异动触发中' : statusLabel(status)}
          </span>
        </div>
        <div className="volume-spike-bars">
          {recentBars.length === 0 ? (
            <div className="volume-spike-empty">等待 Hyperliquid 推送当前事件…</div>
          ) : (
            recentBars.map((c) => {
              const h = Math.max(6, Math.round((Number(c.v) / maxBarVol) * 100));
              const isCurrent = c.t === currentOpen;
              const isPrev = c.t === currentOpen - 60000;
              const isAbsHit = Number(c.v) >= absVolume;
              return (
                <div
                  key={c.t}
                  className={`volume-spike-bar ${isCurrent ? 'is-current' : ''} ${isPrev ? 'is-prev' : ''} ${isAbsHit ? 'is-abs' : ''}`}
                  title={`${formatTime(c.t)} · vol ${formatNum(c.v, 2)} · close ${formatPrice(c.c)}`}
                >
                  <i style={{ height: `${h}%` }} />
                  <span>{formatTime(c.t).slice(0, 5)}</span>
                </div>
              );
            })
          )}
        </div>
      </section>

      <section className="volume-spike-grid">
        <div className="volume-spike-panel">
          <div className="volume-spike-panel-head">
            <div>
              <h2>异动提醒</h2>
              <p>规则 A / 规则 B 独立入列；仅冷启动完成后的实时事件会进入。</p>
            </div>
            <span>{alerts.length} 条</span>
          </div>
          <div className="volume-spike-alerts">
            {alerts.length === 0 ? (
              <div className="volume-spike-empty">
                暂无异动。规则 A：≥ 前分钟 {multiplier} 倍 且当前量 ≥ {formatCompactVol(multipleMinVolume)}；规则 B：单分钟 ≥ {formatCompactVol(absVolume)}。
              </div>
            ) : (
              alerts.map((a) => (
                <article key={a.id} className={`volume-spike-alert ${a.type === 'absolute' ? 'is-abs' : 'is-multiple'}`}>
                  <div>
                    <strong>{formatTime(a.at)}</strong>
                    <span>
                      <em className={`volume-spike-badge tone-${a.type === 'absolute' ? 'warm' : 'bad'}`}>
                        {alertTypeLabel(a.type)}
                      </em>
                      {' '}{coin} · {formatTime(a.currentT)}
                    </span>
                  </div>
                  <div>
                    <b>
                      {a.type === 'absolute'
                        ? formatCompactVol(a.currentVolume)
                        : (Number.isFinite(a.multiple) ? `${a.multiple.toFixed(2)}×` : '-')}
                    </b>
                    <span>
                      {a.type === 'absolute'
                        ? `阈值 ${formatCompactVol(a.threshold)} · 量 ${formatNum(a.currentVolume, 2)}`
                        : `量 ${formatNum(a.currentVolume, 2)} / 前 ${formatNum(a.previousVolume, 2)}`}
                    </span>
                  </div>
                  <div>
                    <b className={Number(a.priceChangePct) > 0 ? 'up-text' : Number(a.priceChangePct) < 0 ? 'down-text' : ''}>
                      {formatPct(a.priceChangePct)}
                    </b>
                    <span>
                      {a.previousPrice != null ? `${formatPrice(a.previousPrice)} → ${formatPrice(a.currentPrice)}` : formatPrice(a.currentPrice)}
                    </span>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>

        <div className="volume-spike-panel">
          <div className="volume-spike-panel-head">
            <div>
              <h2>最近分钟明细</h2>
              <p>来自 WS candle 推送的本地窗口（最多 {MAX_CANDLES} 根）。</p>
            </div>
          </div>
          <div className="volume-spike-table-wrap">
            <table className="volume-spike-table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>成交量</th>
                  <th>收盘</th>
                  <th>笔数</th>
                  <th>标记</th>
                </tr>
              </thead>
              <tbody>
                {[...candles].reverse().slice(0, 12).map((c) => (
                  <tr key={c.t} className={c.t === currentOpen ? 'is-current' : ''}>
                    <td>{formatTime(c.t)}</td>
                    <td>{formatNum(c.v, 2)}</td>
                    <td>{formatPrice(c.c)}</td>
                    <td>{formatNum(c.n, 0)}</td>
                    <td>
                      {c.t === currentOpen ? '当前' : c.t === currentOpen - 60000 ? '前1分' : '-'}
                      {Number(c.v) >= absVolume ? ` · ≥${formatCompactVol(absVolume)}` : ''}
                    </td>
                  </tr>
                ))}
                {candles.length === 0 && (
                  <tr>
                    <td colSpan={5}>暂无数据</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
